import { z } from 'zod';
import { currencyCodeSchema, idSchema, moneySchema } from './common.js';
import { fiscalFxProofSchema, fiscalFxRateSchema } from './fiscal-fx.js';
import { ledgerAccountSchema, ledgerDateSchema } from './financial-accounting.js';
export const apSelfAssessmentCaseSchema = z.enum([
    'IMPORT_GOODS_NO_RECIPIENT_VAT',
    'FOREIGN_LOCAL_VAT_NO_RECIPIENT_VAT',
    'EU_ACQUISITION',
    'EU_SERVICE',
    'RC_FOREIGN_WORK_SERVICE',
    'RC_SECURITY',
    'RC_PROPERTY',
    'RC_CONSTRUCTION',
    'RC_FOREIGN_ENERGY',
    'RC_GAS',
    'RC_ELECTRICITY',
    'RC_EMISSIONS',
    'RC_SCRAP',
    'RC_CLEANING',
    'RC_GOLD',
    'RC_ELECTRONICS',
    'RC_METALS',
    'RC_TELECOM',
]);
export const apSelfAssessmentAllocationSchema = z
    .object({
    position: z.number().int().min(1).max(9999),
    sourcePosition: z.number().int().min(1).max(9999).optional(),
    rate: z.enum(['0', '7', '19']),
    inputTaxAccountId: idSchema,
    outputTaxAccountId: idSchema,
})
    .strict();
export const apSelfAssessmentReviewSchema = z
    .object({
    case: apSelfAssessmentCaseSchema,
    fiscalRateId: idSchema.optional(),
    sourceFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
    reason: z.string().trim().min(10).max(2000),
    eligibilityConfirmed: z.literal(true),
    noSpecialTimingConfirmed: z.literal(true),
    allocations: z.array(apSelfAssessmentAllocationSchema).max(200),
})
    .strict()
    .superRefine((value, context) => {
    const sourcePositions = value.allocations.flatMap((row) => row.sourcePosition === undefined ? [] : [row.sourcePosition]);
    if (new Set(sourcePositions).size !== sourcePositions.length)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: 'apSelfAssessment.invalid',
        });
    const nonRecipient = [
        'IMPORT_GOODS_NO_RECIPIENT_VAT',
        'FOREIGN_LOCAL_VAT_NO_RECIPIENT_VAT',
    ].includes(value.case);
    if (nonRecipient ? value.allocations.length !== 0 : value.allocations.length === 0)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: 'apSelfAssessment.invalid',
        });
    if (new Set(value.allocations.map((row) => row.position)).size !== value.allocations.length)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['allocations'],
            message: 'apSelfAssessment.invalid',
        });
});
export const apSelfAssessmentAmountSchema = z.object({
    position: z.number().int().positive(),
    rate: z.enum(['0', '7', '19']),
    legalTaxAmount: moneySchema,
    deductibleTaxAmount: moneySchema,
    nondeductibleTaxAmount: moneySchema,
    costAmount: moneySchema,
    originalNetAmount: moneySchema.optional(),
    fiscalNetAmount: moneySchema.optional(),
    bookCostAmount: moneySchema.optional(),
});
export const apSelfAssessmentProofSchema = z.object({
    sourceMaterial: z.string().optional(),
    sourceCurrency: currencyCodeSchema.optional(),
    bookExchangeRate: z
        .string()
        .regex(/^\d+(?:\.\d{1,10})?$/u)
        .optional(),
    fiscalFx: fiscalFxProofSchema.optional(),
    revision: z.number().int().positive(),
    review: apSelfAssessmentReviewSchema,
    taxDate: ledgerDateSchema,
    legalBasis: z.string(),
    originalTaxAmount: moneySchema,
    payableAmount: moneySchema,
    legalTaxAmount: moneySchema,
    deductibleTaxAmount: moneySchema,
    costAmount: moneySchema,
    rows: z.array(apSelfAssessmentAmountSchema),
});
export const apSelfAssessmentContextSchema = z.object({
    fiscalRates: z.array(fiscalFxRateSchema).optional(),
    required: z.boolean(),
    sourceFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
    recommendation: apSelfAssessmentCaseSchema.nullable(),
    review: apSelfAssessmentReviewSchema.nullable(),
    proof: apSelfAssessmentProofSchema.nullable(),
    taxAccounts: z.array(ledgerAccountSchema),
    residualPayableAmount: moneySchema.optional(),
    sourceAllocations: z
        .array(z.object({
        position: z.number().int().positive(),
        rate: z.enum(['0', '7', '19']),
        accountNumber: z.string(),
        accountName: z.string(),
        netAmount: moneySchema,
        inputTaxDeductionPercent: z.string(),
    }))
        .optional(),
});
//# sourceMappingURL=ap-self-assessment.js.map